import React, { useContext, useRef } from "react";
import { Input, message, Modal, Radio } from "antd";
import { ModalProps } from "antd/es/modal";
import Form, { useForm, useWatch } from "rc-field-form";
import Editor from "components/Editor";
import PickDuration from "./Components/PickDuration";
import PickShowComment from "./Components/PickShowComment";
import { DiscussTopicForm, saveDiscussTopic } from "service/discuss";
import useRequestFn from "utils/useRequestFn";
import { errorMessage, isTrue } from "service/service";
import { CourseContext } from "../../../CourseDetail/Store";
import { useMount } from "react-use";
import BraftEditor from "braft-editor";
import FieldWithErrorHaveLabel from "../../../../components/FieldWithErrorHaveLabel";

interface TopicEditorProps extends ModalProps {
    initialValues?: DiscussTopicForm;
    onOk(): void;
}

export default function TopicEditor({
    initialValues,
    onOk,
    ...props
}: TopicEditorProps) {
    const [form] = useForm<DiscussTopicForm>();
    const editorRef = useRef<BraftEditor>(null);

    const { course } = useContext(CourseContext);

    useMount(async () => {
        if (!initialValues) return;
        form.setFieldsValue(initialValues);
    });

    const [onFinish, { loading }] = useRequestFn(
        async (data: DiscussTopicForm) => {
            if (!editorRef.current) return;

            if (editorRef.current.getValue().isEmpty()) {
                message.error("主题讨论内容不能为空!");
                return;
            }

            try {
                await saveDiscussTopic(data);

                onOk();

                if (!initialValues?.uuid) {
                    Modal.success({
                        content: (
                            <div>
                                保存成功! 请点击 <b>开始讨论</b> 按钮发布给学生
                            </div>
                        )
                    });
                }
            } catch (e) {
                message.error(errorMessage(e));
            }
        }
    );

    const showStudentName = useWatch("showStudentName", form);

    return (
        <Modal
            title="编辑讨论"
            maskClosable={false}
            width={1000}
            {...props}
            onOk={form.submit}
            confirmLoading={loading}
            centered>
            <Form form={form} onFinish={onFinish}>
                <Form.Field name="uuid">
                    <Input hidden />
                </Form.Field>

                <Form.Field name="weektimeId" initialValue={course.weekTimeId}>
                    <Input hidden />
                </Form.Field>

                <Form.Field name="batchNo" initialValue={course.batchNo}>
                    <Input hidden />
                </Form.Field>

                <Form.Field name="courseNo" initialValue={course.courseNo}>
                    <Input hidden />
                </Form.Field>

                <Form.Field name="courseSeq" initialValue={course.courseSeq}>
                    <Input hidden />
                </Form.Field>

                <FieldWithErrorHaveLabel name="topicName" label="主题讨论名称">
                    <Input
                        placeholder="请输入主题讨论名称，不填写系统将自动按创建时间生成名称"
                        maxLength={100}
                    />
                </FieldWithErrorHaveLabel>

                <FieldWithErrorHaveLabel
                    name="topicContent"
                    label="主题讨论内容"
                    valuePropName="data">
                    <Editor
                        ref={editorRef}
                        data=""
                        placeholder="请输入主题讨论内容，字数不超过6000字"
                    />
                </FieldWithErrorHaveLabel>

                <FieldWithErrorHaveLabel
                    name="topicDuration"
                    label="主题讨论时长"
                    initialValue={0}>
                    <PickDuration />
                </FieldWithErrorHaveLabel>

                <FieldWithErrorHaveLabel align="middle" label="讨论意见展示">
                    <PickShowComment />
                </FieldWithErrorHaveLabel>

                <FieldWithErrorHaveLabel
                    name="showStudentName"
                    label="学生姓名"
                    align="middle"
                    initialValue="1">
                    <Radio.Group>
                        <Radio value="0">不展示</Radio>
                        <Radio value="1">展示</Radio>
                    </Radio.Group>
                </FieldWithErrorHaveLabel>

                {isTrue(showStudentName) && (
                    <FieldWithErrorHaveLabel
                        name="canAnonymousSpeak"
                        align="middle"
                        preserve={false}
                        label="匿名发言"
                        initialValue="1">
                        <Radio.Group>
                            <Radio value="0">禁止</Radio>
                            <Radio value="1">允许</Radio>
                        </Radio.Group>
                    </FieldWithErrorHaveLabel>
                )}

                <FieldWithErrorHaveLabel
                    name="canMutualReplay"
                    label="相互回复"
                    align="middle"
                    initialValue="1">
                    <Radio.Group>
                        <Radio value="0">禁止</Radio>
                        <Radio value="1">允许</Radio>
                    </Radio.Group>
                </FieldWithErrorHaveLabel>
            </Form>
        </Modal>
    );
}
